/**
 * The workflow builder's canvas geometry: node/row metrics and the layered
 * auto-layout that turns a node list plus an edge list into x/y positions.
 *
 * Extracted from the component so the layout rules can be tested directly —
 * importing the component into a test pulls in axios, which the CRA Jest setup
 * cannot transform.
 */

export const SYNTH_UID = "__synth__";

export const NODE_W = 176;      // WORKFLOW_NODE_WIDTH
export const NODE_H = 56;       // NODE_HEIGHT
export const TERMINAL = 40;     // TERMINAL_SIZE — Start / End circles
export const LAYER_GAP = 80;    // vertical gap between rows
export const NODE_GAP = 22;     // horizontal gap inside a row
export const PAD = 36;          // CANVAS_PADDING
export const ROW_PITCH = NODE_H + LAYER_GAP;
export const TERMINAL_INSET = (NODE_H - TERMINAL) / 2;  // centres a terminal in its row band

/**
 * Layered auto-layout, identical in shape to the consumer viewer:
 *
 *  - every node sits one row below its deepest parent (longest-path layering),
 *    so siblings sharing a parent land on the same row = they run in parallel;
 *  - the synthesizer is pinned to the last agent row + 1;
 *  - START occupies row 0 and END the row after the synthesizer;
 *  - inside a row, nodes are ordered by the barycentre of their neighbours so
 *    edges run (near) vertically instead of crossing over each other. The same
 *    order is what `buildPlan` persists, so the viewer reproduces this layout.
 *
 * `hints` — the previous layout's `{ row, slot }` — keep the picture still while
 * the graph is edited: a node holds its row and its place in that row, and only
 * moves when a new edge puts it above a parent. Drawing an edge across rows
 * therefore just draws the edge instead of re-flowing the graph. Barycentre
 * ordering runs only on a fresh layout (no hints), i.e. on load or reset.
 */
export const computeLayout = (agentNodes, edges, hints = null) => {
  const uids = [...agentNodes.map(n => n._uid), SYNTH_UID];
  const known = new Set(uids);
  const inc = {}, out = {}, parents = {};
  uids.forEach(u => { inc[u] = 0; out[u] = []; parents[u] = []; });
  for (const e of edges) {
    if (known.has(e.from) && known.has(e.to)) {
      out[e.from].push(e.to);
      parents[e.to].push(e.from);
      inc[e.to]++;
    }
  }

  // longest-path layering, floored at the row the node held last time
  const pinnedRow = hints?.row || {};
  const deg = { ...inc };
  const layer = {};
  uids.forEach(u => { layer[u] = 0; });
  const q = uids.filter(u => deg[u] === 0);
  const settled = new Set(q);
  const order = [];
  while (q.length) {
    const u = q.shift(); order.push(u);
    // safe to pin here: every parent of `u` has already been processed
    layer[u] = Math.max(layer[u], pinnedRow[u] || 0);
    for (const v of out[u]) {
      // a target always sits below its source — that is what an edge means here
      layer[v] = Math.max(layer[v], layer[u] + 1);
      if (--deg[v] === 0) { q.push(v); settled.add(v); }
    }
  }
  const cyclic = order.length !== uids.length;
  if (cyclic) {
    const base = order.length ? Math.max(...order.map(u => layer[u])) : 0;
    uids.filter(u => !settled.has(u)).forEach(u => { layer[u] = base + 1; });
  }

  const maxAgentLayer = agentNodes.length ? Math.max(...agentNodes.map(n => layer[n._uid])) : -1;
  layer[SYNTH_UID] = maxAgentLayer + 1;

  // close the gaps a pin or a deletion can leave, so rows stay consecutive
  const compact = new Map([...new Set(uids.map(u => layer[u]))].sort((a, b) => a - b).map((l, i) => [l, i]));
  uids.forEach(u => { layer[u] = compact.get(layer[u]); });

  // bucket per layer, keeping the incoming array order as the starting point
  const byLayer = new Map();
  for (const u of uids) {
    const l = layer[u];
    if (!byLayer.has(l)) byLayer.set(l, []);
    byLayer.get(l).push(u);
  }
  const rows = [...byLayer.entries()].sort((a, b) => a[0] - b[0]);

  const slot = {};
  rows.forEach(([, list]) => list.forEach((u, i) => { slot[u] = i; }));

  // barycentre ordering — down, up, down (deterministic, ties keep prior order)
  const sweep = (down) => {
    const seq = down ? rows.slice(1) : rows.slice(0, -1).reverse();
    for (const [, list] of seq) {
      const prior = new Map(list.map((u, i) => [u, i]));
      const bary = {};
      list.forEach((u, i) => {
        const neighbours = (down ? parents[u] : out[u]).filter(v => slot[v] !== undefined);
        bary[u] = neighbours.length
          ? neighbours.reduce((s, v) => s + slot[v], 0) / neighbours.length
          : i;
      });
      list.sort((a, b) => (bary[a] - bary[b]) || (prior.get(a) - prior.get(b)));
      list.forEach((u, i) => { slot[u] = i; });
    }
  };

  if (hints?.slot) {
    // hold the previous left-to-right order; a node added since then lands beside
    // the neighbour it was inserted next to in the node list
    for (const [, list] of rows) {
      const key = {};
      list.forEach(u => { if (hints.slot[u] !== undefined) key[u] = hints.slot[u]; });
      list.forEach((u, i) => {
        if (key[u] !== undefined) return;
        let k;
        for (let j = i - 1; j >= 0 && k === undefined; j--) if (key[list[j]] !== undefined) k = key[list[j]] + 0.5;
        for (let j = i + 1; j < list.length && k === undefined; j++) if (key[list[j]] !== undefined) k = key[list[j]] - 0.5;
        key[u] = k === undefined ? i : k;
      });
      const prior = new Map(list.map((u, i) => [u, i]));
      list.sort((a, b) => (key[a] - key[b]) || (prior.get(a) - prior.get(b)));
      list.forEach((u, i) => { slot[u] = i; });
    }
  } else {
    sweep(true); sweep(false); sweep(true);
  }

  const rowW = list => list.length * NODE_W + (list.length - 1) * NODE_GAP;
  const contentW = Math.max(NODE_W, ...rows.map(([, list]) => rowW(list)));

  // row 0 is START, so an agent on layer L renders on row L + 1
  const pos = {};
  for (const [lv, list] of rows) {
    const y = PAD + (lv + 1) * ROW_PITCH;
    const x0 = PAD + (contentW - rowW(list)) / 2;
    list.forEach((u, i) => { pos[u] = { x: x0 + i * (NODE_W + NODE_GAP), y }; });
  }

  /**
   * `hints.anchorCol` asks for a node to be drawn in the same column as another
   * node — what "add a node below *this* one" means visually. Row order alone
   * cannot express it: every row is centred in `contentW` independently, so a
   * lone node on an otherwise empty row is centred and drifts away from the
   * node it was added under whenever the two rows differ in width.
   *
   * So nudge it after the fact. The node slides to its anchor's x as long as
   * that does not overlap a neighbour already placed on its row; when the
   * column is taken, it keeps its centred position rather than sitting on top
   * of something.
   */
  const wanted = hints?.anchorCol || {};
  for (const [, list] of rows) {
    for (const u of list) {
      const anchor = wanted[u];
      if (!anchor || !pos[anchor] || !pos[u]) continue;
      const x = pos[anchor].x;
      const clash = list.some(v => v !== u && Math.abs(pos[v].x - x) < NODE_W + NODE_GAP - 1);
      if (!clash) pos[u] = { ...pos[u], x };
    }
  }

  /**
   * Hold each node at the x it already had.
   *
   * Rows are centred independently in `contentW`, so widening any one row
   * re-centres every *other* row too: adding a single node to a two-node graph
   * shifted the whole picture sideways, which reads as the layout rearranging
   * itself when the author only meant to add one node. A node that was already
   * on the canvas therefore keeps its column, and only genuinely new nodes take
   * a freshly computed slot.
   *
   * A node is released from its remembered x when its row's contents change
   * around it in a way that would overlap it — the placement above has already
   * chosen non-overlapping slots, so the check is only against the columns this
   * pass has actually fixed.
   */
  const priorX = hints?.x || {};
  for (const [, list] of rows) {
    const fixed = [];
    for (const u of list) {
      // the synthesizer is centred below, never held: it is the one node whose
      // column is a function of the canvas rather than of where it used to be
      if (u === SYNTH_UID) continue;
      const keep = priorX[u];
      if (keep === undefined || !pos[u]) continue;
      // do not re-use a column that another node on this row has already taken
      if (fixed.some(x => Math.abs(x - keep) < NODE_W + NODE_GAP - 1)) continue;
      pos[u] = { ...pos[u], x: keep };
      fixed.push(keep);
    }
  }

  /**
   * Final guarantee: no two nodes on a row may overlap.
   *
   * The three passes above each choose columns for their own reasons — centring
   * places a row, `anchorCol` pulls a node under the one it was added from, and
   * the x-memory holds a node where it already was — and none of them can see
   * the others' results. A held column in particular could land straight on top
   * of a node the centring pass had just put there.
   *
   * So sweep each row left to right and push apart anything closer than one node
   * plus a gap. Order is preserved, the leftmost node keeps its column, and the
   * pass is idempotent: a row that is already clear is left untouched.
   */
  const MIN_DX = NODE_W + NODE_GAP;
  for (const [, list] of rows) {
    const placed = list.filter(u => pos[u]).sort((a, b) => pos[a].x - pos[b].x);
    for (let i = 1; i < placed.length; i++) {
      const prev = pos[placed[i - 1]], cur = pos[placed[i]];
      if (cur.x - prev.x < MIN_DX) pos[placed[i]] = { ...cur, x: prev.x + MIN_DX };
    }
  }

  /**
   * The synthesizer sits centred, always.
   *
   * It is the single point every branch converges on, so it reads as the foot of
   * the graph rather than as one more node in a row — and START and END are
   * centred on the same axis. Left to the passes above it kept whatever column
   * it happened to have when the canvas was narrower, drifting further off
   * centre with every node added to the side.
   *
   * Centred on the *content*, not on the final width, so it lines up with START
   * and END even once a detoured edge has widened the drawing surface.
   */
  if (pos[SYNTH_UID]) {
    pos[SYNTH_UID] = { ...pos[SYNTH_UID], x: PAD + (contentW - NODE_W) / 2 };
  }

  // the canvas has to span wherever the held columns actually put the nodes
  const maxNodeRight = Math.max(
    NODE_W,
    ...Object.values(pos).map(p => p.x + NODE_W),
  );
  const width = Math.max(contentW + PAD * 2, maxNodeRight + PAD);

  const lastRow = (rows.length ? rows[rows.length - 1][0] : 0) + 1;
  const endTop = PAD + (lastRow + 1) * ROW_PITCH + TERMINAL_INSET;
  const cx = width / 2;

  return {
    pos, layer, incoming: inc, cyclic,
    width,
    height: endTop + TERMINAL + PAD,
    /** fed back in on the next edit to keep rows and columns where they are */
    hints: {
      row: { ...layer }, slot: { ...slot }, anchorCol: { ...wanted },
      /** each node's column, so a later edit does not re-centre the graph */
      x: Object.fromEntries(Object.entries(pos).map(([u, p]) => [u, p.x])),
    },
    /**
     * Entry points, which START draws an arrow to. A node the author dropped
     * under a specific node via its `+` port is deliberately excluded even
     * though nothing points at it yet: it is unwired, not an entry point, and
     * drawing START → it would run a long line down the canvas from the top
     * rather than from the node whose `+` was clicked. It stops being a special
     * case as soon as it has a real parent, or if its anchor is deleted.
     */
    /**
     * Entry points — the nodes START draws an arrow to.
     *
     * Only the *first level* starts the workflow. A parentless node further down
     * is one the author has not wired up yet, not an entry point: drawing START
     * to it would run a long line from the top of the canvas past everything in
     * between, and would claim the graph begins in two places at once. Such a
     * node is surfaced as an "not connected" warning instead, which is the
     * honest signal.
     *
     * A node added *above* the current first level therefore becomes an entry
     * point on its own, with no extra wiring: it is now the topmost row, so the
     * rule below picks it up.
     *
     * The synthesizer is never an entry point — it collects the graph. With no
     * upstream edges it would otherwise qualify, and selecting it would light a
     * line straight down the middle of the canvas from START through it to END.
     */
    roots: (() => {
      const candidates = uids.filter(u => inc[u] === 0 && u !== SYNTH_UID);
      if (!candidates.length) return [];
      const topRow = Math.min(...candidates.map(u => layer[u]));
      return candidates.filter(u => layer[u] === topRow);
    })(),
    /** agent uids in render order (row by row, left to right) — persisted as-is */
    orderedUids: rows.flatMap(([, list]) => list).filter(u => u !== SYNTH_UID),
    start: { x: cx - TERMINAL / 2, y: PAD + TERMINAL_INSET },
    end: { x: cx - TERMINAL / 2, y: endTop },
  };
};

